import React from 'react';
import { Box, Typography } from '@mui/material';

/**
 * Circular gauge with gradient stroke. Pure SVG, no deps.
 * value: 0..100 (percent). size: px. label: caption.
 * tone: 'good' | 'warn' | 'crit' | 'muted' (auto-derived if omitted).
 */
export default function MetricGauge({
    value,
    size = 92,
    stroke = 9,
    label,
    valueLabel,
    tone,
    warn = 70,
    crit = 85,
    invert = false,
}) {
    const v = Number.isFinite(value) ? Math.max(0, Math.min(100, value)) : null;
    const effective = v == null ? 0 : v;
    const triggerValue = invert ? 100 - effective : effective;
    const autoTone = v == null ? 'muted' : (triggerValue >= crit ? 'crit' : triggerValue >= warn ? 'warn' : 'good');
    const t = tone || autoTone;

    const radius = (size - stroke) / 2;
    const circ = 2 * Math.PI * radius;
    const dash = (effective / 100) * circ;
    const colors = {
        good:  { from: '#10b981', to: '#34d399', glow: 'rgba(16,185,129,0.35)' },
        warn:  { from: '#f59e0b', to: '#fbbf24', glow: 'rgba(245,158,11,0.35)' },
        crit:  { from: '#ef4444', to: '#f87171', glow: 'rgba(239,68,68,0.45)' },
        muted: { from: '#64748b', to: '#94a3b8', glow: 'rgba(100,116,139,0.25)' },
    }[t];
    const gid = `g-${t}-${size}`;

    const display = valueLabel != null
        ? valueLabel
        : (v == null ? '—' : `${Math.round(v)}%`);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ position: 'relative', width: size, height: size }}>
                <svg width={size} height={size} style={{ filter: `drop-shadow(0 0 12px ${colors.glow})` }}>
                    <defs>
                        <linearGradient id={gid} x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" stopColor={colors.from} />
                            <stop offset="100%" stopColor={colors.to} />
                        </linearGradient>
                    </defs>
                    <circle
                        cx={size / 2} cy={size / 2} r={radius}
                        fill="none"
                        stroke="rgba(148,163,184,0.18)"
                        strokeWidth={stroke}
                    />
                    <circle
                        cx={size / 2} cy={size / 2} r={radius}
                        fill="none"
                        stroke={`url(#${gid})`}
                        strokeWidth={stroke}
                        strokeDasharray={`${dash} ${circ - dash}`}
                        strokeDashoffset={circ / 4}
                        strokeLinecap="round"
                        transform={`rotate(-90 ${size / 2} ${size / 2})`}
                        style={{ transition: 'stroke-dasharray 600ms cubic-bezier(.4,0,.2,1)' }}
                    />
                </svg>
                <Box sx={{
                    position: 'absolute', inset: 0, display: 'flex',
                    alignItems: 'center', justifyContent: 'center',
                    flexDirection: 'column',
                }}>
                    <Typography sx={{
                        fontSize: size < 80 ? 14 : 18,
                        fontWeight: 700,
                        fontVariantNumeric: 'tabular-nums',
                        lineHeight: 1,
                    }}>{display}</Typography>
                </Box>
            </Box>
            {label && (
                <Typography variant="caption" sx={{
                    textTransform: 'uppercase',
                    letterSpacing: 0.7,
                    fontSize: 10,
                    opacity: 0.75,
                    fontWeight: 600,
                }}>{label}</Typography>
            )}
        </Box>
    );
}
